import axios from 'axios'
import {
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authSecurityClearLogHost, authSecurityHost, authSecurityResetHost } from 'utils/Hosts'

import { ClientAddress, ClientLabelEditor } from './ClientLabelEditor'
import { SecondarySettingsContent, SettingSectionLabel } from './style'

const emptySnapshot = {
  failed_day: 0,
  failed_week: 0,
  failed_month: 0,
  daily_lockdown_enabled: false,
  daily_lockdown_active: false,
  daily_failure_limit: 100,
  blocking_active: false,
  blocking_until: null,
  retry_after_seconds: 0,
  blocking_source: '',
  blocking_source_label: '',
  blocking_forwarded_for: '',
  blocking_forwarded_for_label: '',
  active_sessions: 0,
  session_window_seconds: 600,
  updated_at: null,
  audit_file: 'auth-audit.json',
  event_limit: 500,
  retention_days: 30,
  client_labels: {},
  events: [],
  sessions: [],
}

const formatDate = value => (value ? new Date(value).toLocaleString() : '—')

const tableHeaderStyle = {
  fontWeight: 600,
  whiteSpace: 'nowrap',
  background: 'rgba(127, 127, 127, 0.12)',
}

const compactTextStyle = {
  maxWidth: '240px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const splitReportedAddresses = value =>
  String(value || '')
    .split(',')
    .map(address => address.trim())
    .filter(Boolean)

const SourceDetails = ({ item, t, onEdit, labels }) => {
  const proxyAddresses = splitReportedAddresses(item.forwarded_for)
  return (
    <div style={{ minWidth: '145px', maxWidth: '220px' }}>
      <ClientAddress address={item.source} label={item.source_label} onEdit={onEdit} dense />
      {proxyAddresses.length > 0 && (
        <div
          style={{
            marginTop: '4px',
            paddingTop: '3px',
            borderTop: '1px solid rgba(127,127,127,0.16)',
          }}
        >
          <Typography variant='caption' color='textSecondary' style={{ display: 'block', lineHeight: 1.15 }}>
            {t('SecurityAudit.ProxyAddress')}
          </Typography>
          {proxyAddresses.map(address => (
            <ClientAddress
              key={address}
              address={address}
              label={labels?.[address] || (proxyAddresses.length === 1 ? item.forwarded_for_label : '')}
              onEdit={onEdit}
              dense
            />
          ))}
        </div>
      )}
    </div>
  )
}

const applyClientLabels = (current, labels) => {
  const labelFor = address => (address && labels[address]) || ''
  return {
    ...current,
    client_labels: labels,
    blocking_source_label: labelFor(current.blocking_source),
    blocking_forwarded_for_label: labelFor(current.blocking_forwarded_for),
    events: current.events.map(event => ({
      ...event,
      source_label: labelFor(event.source),
      forwarded_for_label: labelFor(event.forwarded_for),
    })),
    sessions: current.sessions.map(session => ({
      ...session,
      source_label: labelFor(session.source),
      forwarded_for_label: labelFor(session.forwarded_for),
    })),
  }
}

export default function SecurityAuditSettings() {
  const { t } = useTranslation()
  const [snapshot, setSnapshot] = useState(emptySnapshot)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [resetting, setResetting] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [labelEditor, setLabelEditor] = useState({
    open: false,
    address: '',
    label: '',
  })

  useEffect(() => {
    let mounted = true
    let requestRunning = false

    const load = async () => {
      if (requestRunning) return
      requestRunning = true
      try {
        const { data } = await axios.get(authSecurityHost())
        if (mounted) {
          setSnapshot({ ...emptySnapshot, ...data })
          setError('')
        }
      } catch (requestError) {
        if (mounted) {
          setError(requestError.response?.data?.error || t('SecurityAudit.LoadError'))
        }
      } finally {
        requestRunning = false
        if (mounted) setLoading(false)
      }
    }

    load()
    const timer = window.setInterval(() => {
      if (!document.hidden) load()
    }, 3000)
    return () => {
      mounted = false
      window.clearInterval(timer)
    }
  }, [refreshVersion, t])

  const editClientLabel = (address, label) => {
    setLabelEditor({ open: true, address, label })
  }

  const clientLabelSaved = async labels => {
    setSnapshot(current => applyClientLabels(current, labels))
    setRefreshVersion(current => current + 1)
  }

  const cards = [
    {
      label: t('SecurityAudit.Day'),
      value: snapshot.failed_day,
      color: '#ef6c00',
    },
    {
      label: t('SecurityAudit.Week'),
      value: snapshot.failed_week,
      color: '#d84315',
    },
    {
      label: t('SecurityAudit.Month'),
      value: snapshot.failed_month,
      color: '#c62828',
    },
    {
      label: t('SecurityAudit.ActiveSessions'),
      value: snapshot.active_sessions,
      color: '#2e7d32',
    },
  ]

  const eventLabel = kind => {
    if (kind === 'lockdown') return t('SecurityAudit.Lockdown')
    if (kind === 'throttled') return t('SecurityAudit.Throttled')
    if (kind === 'busy') return t('SecurityAudit.Busy')
    return t('SecurityAudit.Failed')
  }

  const eventColor = kind => {
    if (kind === 'lockdown') return { background: '#b71c1c', color: '#fff' }
    if (kind === 'throttled') return { background: '#ef6c00', color: '#fff' }
    if (kind === 'busy') return { background: '#546e7a', color: '#fff' }
    return { background: '#c62828', color: '#fff' }
  }

  const resetSecurity = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('SecurityAudit.ResetConfirm'))) return
    setResetting(true)
    try {
      const { data } = await axios.post(authSecurityResetHost())
      setSnapshot({ ...emptySnapshot, ...data })
      setError('')
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('SecurityAudit.ResetError'))
    } finally {
      setResetting(false)
    }
  }

  const clearLog = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('SecurityAudit.ClearLogConfirm'))) return
    setClearing(true)
    try {
      const { data } = await axios.post(authSecurityClearLogHost())
      setSnapshot({ ...emptySnapshot, ...data })
      setError('')
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('SecurityAudit.ClearLogError'))
    } finally {
      setClearing(false)
    }
  }

  return (
    <SecondarySettingsContent>
      <SettingSectionLabel>{t('SecurityAudit.Settings')}</SettingSectionLabel>

      {loading && <CircularProgress color='secondary' size={28} />}
      {error && <FormHelperText error>{error}</FormHelperText>}

      <Grid container spacing={2} style={{ marginTop: '2px', marginBottom: '14px' }}>
        {cards.map(card => (
          <Grid item xs={6} sm={3} key={card.label}>
            <Paper
              variant='outlined'
              style={{
                padding: '14px 12px',
                textAlign: 'center',
                height: '100%',
                borderRadius: '12px',
                borderTop: `4px solid ${card.color}`,
                background: 'rgba(127, 127, 127, 0.035)',
              }}
            >
              <Typography variant='h5' style={{ fontWeight: 700, color: card.color }}>
                {card.value}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {card.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {snapshot.daily_lockdown_enabled && (
        <Paper
          variant='outlined'
          style={{
            padding: '12px',
            marginBottom: '16px',
            borderRadius: '12px',
            borderColor: snapshot.daily_lockdown_active ? '#d32f2f' : '#ed6c02',
            background: snapshot.daily_lockdown_active ? 'rgba(211, 47, 47, 0.07)' : 'rgba(237, 108, 2, 0.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <Chip
                size='small'
                color={snapshot.daily_lockdown_active ? 'secondary' : 'default'}
                label={
                  snapshot.daily_lockdown_active
                    ? t('SecurityAudit.DailyLockdownActive')
                    : t('SecurityAudit.DailyLockdownArmed')
                }
              />
              <Typography variant='body2' style={{ marginTop: '6px' }}>
                {t('SecurityAudit.DailyLockdownProgress', {
                  failures: snapshot.failed_day,
                  limit: snapshot.daily_failure_limit,
                })}
              </Typography>
            </div>
          </div>
          {snapshot.daily_lockdown_active && (
            <FormHelperText error style={{ marginTop: '6px' }}>
              {t('SecurityAudit.DailyLockdownExplanation')}
            </FormHelperText>
          )}
        </Paper>
      )}

      <Paper
        variant='outlined'
        style={{
          padding: '12px',
          marginBottom: '16px',
          borderRadius: '12px',
          borderColor: snapshot.blocking_active ? '#d32f2f' : '#2e7d32',
          background: snapshot.blocking_active ? 'rgba(211, 47, 47, 0.07)' : 'rgba(46, 125, 50, 0.055)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <Chip
            size='small'
            color={snapshot.blocking_active ? 'secondary' : 'default'}
            label={snapshot.blocking_active ? t('SecurityAudit.BlockingActive') : t('SecurityAudit.NoBlocking')}
          />
          {snapshot.blocking_active && (
            <Typography variant='body2'>
              {t('SecurityAudit.BlockingRemaining', {
                seconds: snapshot.retry_after_seconds,
                until: formatDate(snapshot.blocking_until),
              })}
            </Typography>
          )}
          {snapshot.blocking_active && snapshot.blocking_source && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flexWrap: 'wrap',
              }}
            >
              <Typography variant='body2'>{t('SecurityAudit.BlockingSourceShort')}:</Typography>
              <ClientAddress
                address={snapshot.blocking_source}
                label={snapshot.blocking_source_label}
                onEdit={editClientLabel}
                dense
              />
              {snapshot.blocking_forwarded_for && (
                <>
                  <Typography variant='caption' color='textSecondary'>
                    {t('SecurityAudit.ProxyAddress')}:
                  </Typography>
                  {splitReportedAddresses(snapshot.blocking_forwarded_for).map(address => (
                    <ClientAddress
                      key={address}
                      address={address}
                      label={
                        snapshot.client_labels?.[address] ||
                        (address === snapshot.blocking_forwarded_for ? snapshot.blocking_forwarded_for_label : '')
                      }
                      onEdit={editClientLabel}
                      dense
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </Paper>

      <Paper
        variant='outlined'
        style={{
          padding: '12px',
          marginBottom: '18px',
          borderRadius: '12px',
          borderColor: 'rgba(127, 127, 127, 0.28)',
          background: 'rgba(127, 127, 127, 0.035)',
        }}
      >
        <Typography variant='subtitle1' style={{ fontWeight: 600, marginBottom: '8px' }}>
          {t('SecurityAudit.ManualActions')}
        </Typography>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button
            onClick={resetSecurity}
            color='secondary'
            variant='outlined'
            disabled={
              resetting ||
              clearing ||
              (snapshot.failed_month === 0 && !snapshot.blocking_active && !snapshot.daily_lockdown_active)
            }
          >
            {resetting ? t('SecurityAudit.Resetting') : t('SecurityAudit.ResetAttempts')}
          </Button>
          <Button
            onClick={clearLog}
            color='secondary'
            variant='outlined'
            disabled={clearing || resetting || snapshot.events.length === 0}
          >
            {clearing ? t('SecurityAudit.ClearingLog') : t('SecurityAudit.ClearLog')}
          </Button>
        </div>
        <FormHelperText style={{ marginTop: '8px' }}>{t('SecurityAudit.ManualActionsHint')}</FormHelperText>
      </Paper>

      <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
        {t('SecurityAudit.ActiveSessionList')}
      </Typography>
      <FormHelperText style={{ marginBottom: '8px' }}>
        {t('SecurityAudit.SessionHint', {
          minutes: Math.round(snapshot.session_window_seconds / 60),
        })}
      </FormHelperText>

      <TableContainer
        component={Paper}
        variant='outlined'
        style={{
          maxHeight: '230px',
          marginBottom: '22px',
          borderRadius: '12px',
        }}
      >
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.Client')}</TableCell>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.Source')}</TableCell>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.LastSeen')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {snapshot.sessions.length ? (
              snapshot.sessions.slice(0, 50).map(session => (
                <TableRow
                  hover
                  key={`${session.first_seen}-${session.client}-${session.source}-${session.forwarded_for || ''}`}
                >
                  <TableCell title={session.client}>
                    <div style={compactTextStyle}>{session.client}</div>
                    <Typography variant='caption' color='textSecondary'>
                      {session.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <SourceDetails item={session} t={t} onEdit={editClientLabel} labels={snapshot.client_labels} />
                  </TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{formatDate(session.last_seen)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>{t('SecurityAudit.NoSessions')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
          {t('SecurityAudit.LiveLog')}
        </Typography>
        <Chip
          size='small'
          label={t('SecurityAudit.Live')}
          style={{
            color: '#1b5e20',
            background: 'rgba(46, 125, 50, 0.14)',
            fontWeight: 600,
          }}
        />
        <Typography variant='caption'>{formatDate(snapshot.updated_at)}</Typography>
      </div>
      <FormHelperText style={{ marginBottom: '8px' }}>
        {t('SecurityAudit.LogHint', { path: snapshot.audit_file })}
        <br />
        {t('SecurityAudit.RetentionHint', {
          days: snapshot.retention_days,
          limit: snapshot.event_limit,
        })}
      </FormHelperText>

      <TableContainer component={Paper} variant='outlined' style={{ maxHeight: '390px', borderRadius: '12px' }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.Time')}</TableCell>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.Result')}</TableCell>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.Source')}</TableCell>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.Client')}</TableCell>
              <TableCell style={tableHeaderStyle}>{t('SecurityAudit.Path')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {snapshot.events.length ? (
              snapshot.events.slice(0, 100).map(event => (
                <TableRow hover key={event.id}>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{formatDate(event.time)}</TableCell>
                  <TableCell style={{ minWidth: '170px' }}>
                    <Chip
                      size='small'
                      label={`${eventLabel(event.kind)}${event.count > 1 ? ` \u00d7${event.count}` : ''}`}
                      style={{ ...eventColor(event.kind), fontWeight: 600 }}
                    />
                    <Typography variant='caption' color='textSecondary' style={{ display: 'block', marginTop: '3px' }}>
                      HTTP {event.status}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <SourceDetails item={event} t={t} onEdit={editClientLabel} labels={snapshot.client_labels} />
                  </TableCell>
                  <TableCell title={event.client}>
                    <div style={compactTextStyle}>{event.client}</div>
                  </TableCell>
                  <TableCell title={event.path}>
                    <div style={{ ...compactTextStyle, fontFamily: 'monospace' }}>{event.path}</div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5}>{t('SecurityAudit.NoEvents')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ClientLabelEditor
        open={labelEditor.open}
        address={labelEditor.address}
        label={labelEditor.label}
        onClose={() => setLabelEditor(current => ({ ...current, open: false }))}
        onSaved={clientLabelSaved}
      />
    </SecondarySettingsContent>
  )
}
